import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ColumnsObject, ColumnsTemplate } from '../../bulk-export-v2/bulk-export-v2.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { BehaviorSubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AddEditExportColumnsTemplateComponent } from './add-edit-export-columns-template/add-edit-export-columns-template.component';
import { OrderApiService } from '~services/api/order.api.service';
import { ChoiceDialogComponent } from '../choice-dialog/choice-dialog.component';

@Component({
  selector: 'sa-bulk-export-columns',
  templateUrl: './bulk-export-columns.component.html',
  styleUrls: ['./bulk-export-columns.component.scss'],
})
export class BulkExportColumnsComponent implements OnInit, AfterViewInit {
  public _columns: ColumnsObject[];
  public allChecked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public templateList = new BehaviorSubject<ColumnsTemplate[]>(null);
  public selectedTemplate: ColumnsTemplate;
  public tempTemplate: ColumnsTemplate;
  public isFetchingTemplate = false;
  private newlyAddedTemplateId: number = null;
  @Output() onColumnsUpdated: EventEmitter<ColumnsObject[]> = new EventEmitter<ColumnsObject[]>();
  @Output() onSaveTempColumns: EventEmitter<ColumnsObject[]> = new EventEmitter<ColumnsObject[]>();
  @Output() onSaveTempSelectedTemplate: EventEmitter<ColumnsTemplate> = new EventEmitter<ColumnsTemplate>();
  @Output() onAddNewTemplate: EventEmitter<ColumnsTemplate> = new EventEmitter<ColumnsTemplate>();
  @Output() onUpdateTemplate: EventEmitter<ColumnsTemplate> = new EventEmitter<ColumnsTemplate>();
  @Output() onDeleteTemplate: EventEmitter<number> = new EventEmitter<number>();
  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() onShowUpdateTemplateButton: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input('columns')
  set columns(columns: ColumnsObject[]) {
    this._columns = columns;
  }

  @Input('fetchExportColumnTemplate')
  set fetchExportColumnTemplate(fetch: boolean) {
    if (fetch) {
      this.setupExportColumnTemplates();
    }
  }

  @Input('tempSelectedTemplate')
  set tempSelectedTemplate(columnsTemplate: ColumnsTemplate) {
    if (columnsTemplate) {
      this.tempTemplate = columnsTemplate;
    }
  }

  @Input('addedTemplate')
  set addedTemplate(id: number) {
    if (id) {
      this.newlyAddedTemplateId = id;
    }
  }

  constructor(private cdr: ChangeDetectorRef, private dialog: MatDialog, private orderApiService: OrderApiService) {}

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  public ngOnInit(): void {
    this.setupExportColumnTemplates();
  }

  closeClicked() {
    this.onSaveTempColumns.emit(this._columns);
    this.onSaveTempSelectedTemplate.emit(this.selectedTemplate);
    this.onClose.emit();
  }

  onCheckboxChange(col: ColumnsObject, e: MatCheckboxChange) {
    const checkedItems = this._columns.filter((item) => item.checked);
    col.checked = e.checked;
    this.selectedTemplate = null;
    this.onShowUpdateTemplateButton.emit(false);
  }

  onAllCheckboxChange(e: MatCheckboxChange) {
    this._columns.forEach((col) => (col.checked = e.checked));
    this.allChecked.next(e.checked);
  }

  updateColumnSelection() {
    this.onColumnsUpdated.emit(this._columns);
    this.closeClicked();
  }

  setupExportColumnTemplates() {
    this.isFetchingTemplate = true;
    this.orderApiService.getAllExportColumnTemplates().subscribe((templates) => {
      this.templateList.next(templates);
      this.isFetchingTemplate = false;

      if (this.tempTemplate) {
        this.selectedTemplate = this.templateList.value.find((template) => template.id === this.tempTemplate.id);
      }

      if (this.newlyAddedTemplateId) {
        this.selectedTemplate = this.templateList.value.find((template) => template.id === this.newlyAddedTemplateId);
        this.newlyAddedTemplateId = null;
        const columns = JSON.parse(this.selectedTemplate.columns);
        this._columns = this._columns.map((col) => {
          const matchingColumn = columns.find((c) => c.name === col.name);
          if (matchingColumn) {
            return { ...col, checked: matchingColumn.checked };
          } else {
            return { ...col, checked: false };
          }
        });
      }
    });
  }

  onTemplateSelected(value: ColumnsTemplate) {
    this.selectedTemplate = value;
    this._columns = JSON.parse(value.columns);
  }

  onOptionClicked(value: ColumnsTemplate) {
    if (this.selectedTemplate === value) {
      this.onTemplateSelected(value);
    }
  }

  promptSaveAsTemplate() {
    const columnsTemplate = this._columns.map((column, index) => ({
      id: (index + 1).toString(),
      name: column.name,
      checked: column.checked,
      position: (index + 1).toString(),
    }));

    const dialogRef = this.dialog.open(AddEditExportColumnsTemplateComponent, {
      data: {
        templateId: '',
        templateName: '',
        templateList: columnsTemplate,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.update) {
          this.updateTemplate(result);
        } else {
          this.addNewTemplate(result);
        }
      }
    });
  }

  promptEditTemplate(event: Event) {
    event.stopPropagation();
    const columnsTemplate = this._columns.map((column, index) => ({
      id: (index + 1).toString(),
      name: column.name,
      checked: column.checked,
      position: column.position,
    }));

    const dialogRef = this.dialog.open(AddEditExportColumnsTemplateComponent, {
      data: {
        templateId: this.selectedTemplate.id,
        templateName: this.selectedTemplate.templateName,
        templateList: columnsTemplate,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.update) {
          this.updateTemplate(result);
        } else {
          this.addNewTemplate(result);
        }
      }
    });
  }

  promptDeleteTemplate(event: Event) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ChoiceDialogComponent, {
      data: {
        button: ['No', 'Yes'],
        context: `Are you sure you want to remove ${this.selectedTemplate.templateName}`,
        desc: '',
      },
      maxWidth: '968px',
      width: '30%',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteTemplate();
      }
    });
  }

  public updateTemplate(result) {
    this.onUpdateTemplate.emit(result.data);
  }

  public addNewTemplate(result) {
    this.onAddNewTemplate.emit(result.data);
  }

  public deleteTemplate() {
    const templateId = this.selectedTemplate.id;
    this.onDeleteTemplate.emit(templateId);
    this.tempTemplate = null;
  }
}
